import React, { useState, useEffect, useRef } from 'react';
import { ChatBubbleLeftRightIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { useQuery, useMutation } from '../../hooks';
import { chatAPI } from '../../services/api';
import { Card, SectionHeader, Button, Badge, StatusBadge, LoadingPage, EmptyState, Avatar } from '../../components/common/UI';
import { useSelector } from 'react-redux';
import { selectUser } from '../../store/slices/authSlice';
import { format } from 'date-fns';

const PRIORITY_BADGE = { low: 'gray', normal: 'blue', high: 'yellow', urgent: 'red' };

export default function BusinessChat({ business }) {
  const [selectedRoom, setSelected] = useState(null);
  const [msg, setMsg] = useState('');
  const me = useSelector(selectUser);
  const bottomRef = useRef(null);

  const { data: roomsData, loading: roomsLoading } = useQuery(() => chatAPI.adminRooms({ limit: 50 }), []);
  const { data: msgsData, loading: msgsLoading, refetch: refetchMsgs } = useQuery(
    () => chatAPI.messages(selectedRoom?.id, { limit: 100 }),
    [selectedRoom?.id],
    { enabled: !!selectedRoom }
  );

  const { mutate: send, loading: sending } = useMutation(
    () => chatAPI.sendMessage(selectedRoom.id, { message: msg }),
    { errorMsg: false, onSuccess: () => { setMsg(''); refetchMsgs(); } }
  );

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgsData]);

  const rooms    = roomsData?.data || [];
  const messages = msgsData?.data || [];

  const handleKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (msg.trim()) send(); } };

  return (
    <div className="animate-in">
      <SectionHeader title="Customer Support" subtitle="Respond to customer inquiries" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-180px)]">
        {/* Room list */}
        <Card className="overflow-hidden flex flex-col p-0">
          <div className="p-4 border-b border-surface-100">
            <h3 className="font-display font-semibold text-surface-900">Conversations</h3>
          </div>
          <div className="flex-1 overflow-y-auto">
            {roomsLoading ? <LoadingPage /> : rooms.length === 0 ? (
              <EmptyState icon={ChatBubbleLeftRightIcon} title="No conversations" />
            ) : rooms.map(r => (
              <button key={r.id} onClick={() => setSelected(r)}
                className={`w-full text-left p-4 border-b border-surface-50 hover:bg-surface-50 transition-colors ${selectedRoom?.id === r.id ? 'bg-brand-50 border-l-2 border-l-brand-500' : ''}`}>
                <div className="flex items-start gap-2.5">
                  <Avatar name={r.customer?.first_name || 'C'} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-surface-900 truncate">{r.customer?.first_name} {r.customer?.last_name}</p>
                      <Badge variant={PRIORITY_BADGE[r.priority]}>{r.priority}</Badge>
                    </div>
                    <p className="text-xs text-surface-400 truncate mt-0.5">{r.subject || 'No subject'}</p>
                    <StatusBadge status={r.status} />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Chat window */}
        <div className="lg:col-span-2">
          {selectedRoom ? (
            <Card className="h-full overflow-hidden p-0 flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-surface-100 p-4">
                <div>
                  <p className="font-semibold text-surface-900">{selectedRoom.subject || 'Support Chat'}</p>
                  <StatusBadge status={selectedRoom.status} />
                </div>
                <Button variant="secondary" size="sm" onClick={() => chatAPI.close(selectedRoom.id).then(() => setSelected(null))}>
                  Close Chat
                </Button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {msgsLoading ? <LoadingPage /> : messages.length === 0 ? (
                  <p className="text-center text-sm text-surface-400 py-8">No messages yet</p>
                ) : messages.map(m => {
                  const isMine = m.sender_id === me?.id;
                  return (
                    <div key={m.id} className={`flex gap-2 ${isMine ? 'flex-row-reverse' : ''}`}>
                      <Avatar name={`${m.sender?.first_name || ''}`} size="sm" className="flex-shrink-0" />
                      <div className={`max-w-[72%] rounded-2xl px-4 py-2.5 text-sm ${isMine ? 'bg-brand-600 text-white rounded-tr-sm' : 'bg-surface-100 text-surface-800 rounded-tl-sm'}`}>
                        {!isMine && <p className="text-[10px] font-semibold mb-1 text-brand-600">{m.sender?.first_name}</p>}
                        <p className="leading-relaxed">{m.message}</p>
                        <p className={`text-[10px] mt-1 ${isMine ? 'text-brand-200' : 'text-surface-400'}`}>{m.created_at ? format(new Date(m.created_at), 'HH:mm') : ''}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div className="border-t border-surface-100 p-4 flex items-end gap-2">
                <textarea
                  className="input flex-1 resize-none min-h-[40px] max-h-[120px]"
                  placeholder="Type a message… (Enter to send)"
                  value={msg}
                  onChange={e => setMsg(e.target.value)}
                  onKeyDown={handleKey}
                  rows={1}
                />
                <Button variant="primary" size="md" icon={PaperAirplaneIcon} loading={sending} onClick={() => msg.trim() && send()} disabled={!msg.trim()} />
              </div>
            </Card>
          ) : (
            <div className="flex h-full items-center justify-center text-surface-400">
              <div className="text-center">
                <ChatBubbleLeftRightIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Select a conversation to reply</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
